import styles from './stockForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { PanelWrapper, FieldType, FieldValue, Form, FormField, StockAction, option, useUIStateActions, useGetCrumbs, Badge, NumberField, O, Async } from "../../../../features/ui"
import { define } from "../../../../utils/typeUtils"
import { Stock } from "../../state/types"
import { Lot, Product, ProductName, ProductType, useLotLoaders, useProductHooks, useProductLoaders } from "../../../products"
import { TableName } from "../../../../tables"
import { useActions } from './actions'
import { useBranchLoaders } from '../../../manager'
import { useNavigate } from 'react-router-dom'
import { RoutePath, useRoutesHooks } from '../../../../features/routes'
import { firstItem } from '../../../../utils/listUtils'
import { useStockHooks } from '../../hooks/stockHooks'
import { useStockLoaders } from '../../loaders/stockLoaders'

type Props = {
    stockAction?: StockAction
}

export const StockForm = (props: Props) => {
    const { stockAction = StockAction.ADD } = props
    const addMode = stockAction !== StockAction.SUBSTRACT
    const { dataType } = useRoutesHooks().parseUrl()
    const stock = define(dataType) as Stock

    const [product, setProduct] = useState<Product>()
    const [lots, setLots] = useState<Lot[]>([])
    const [lot, setLot] = useState<Lot>()
    const [amount, setAmount] = useState<number>()
    const [loaded, setLoaded] = useState(false)

    const { describeAvailableStock } = useStockHooks()
    const { describeUnitType } = useProductHooks()
    const navigate = useNavigate()

    const branchLoaders = useBranchLoaders()
    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()
    const stockLoaders = useStockLoaders()

    const { updateStock } = useActions()
    const { setDisableBranchSelector } = useUIStateActions()
    
    const load = async () => {
        setDisableBranchSelector(true)
        const { loadProduct } = productLoaders()
        const loadedProduct = define(await loadProduct(stock.productId))
        setProduct(loadedProduct)
        const { loadProductLots } = lotLoaders()
        const loadedLots = await loadProductLots(loadedProduct.id)
        setLots(loadedLots)
        setLot(firstItem(loadedLots))
    }

    useEffect(() => {
        load().then(() => setLoaded(true))
        return () => {
            setDisableBranchSelector(false)
        }
    }, [])

    const isBulk = useMemo(() => product?.type === ProductType.BULK, [product?.type])

    const title = `${addMode ? 'Alta' : 'Baja'} de Stock: ${product?.name}`

    const crumbs = useGetCrumbs(
        'Stock',
        RoutePath.STOCK,
        product?.name,
        stock.id,
        addMode ? 'Alta' : 'Baja'
    )

    const onChangeLot = (value?: FieldValue) => setLot(lots.find(productLot => productLot.id === value))

    const onChangeAmount = (value?: FieldValue) => setAmount(value as O<number>)

    const fields: FormField[] = [
        {
            name: 'product',
            type: FieldType.TEXT,
            label: 'Producto',
            render: () => <ProductName product={product} showCode />
        },
        {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: 'Sucursal',
            render: () => {
                const branch = branchLoaders().findBranch(stock.branchId)
                return <Badge label={define(branch).name} />
            }
        },
        {
            name: 'describeAvailableStock',
            type: FieldType.NUMBER,
            label: 'Stock Disponible',
            loading: !loaded,
            render: () => <Async
                loadFn={async () => <>{await describeAvailableStock(define(product), stock.branchId, define(lot).id)}</>}
                deps={[product, stock, lot]}
            />
        },
        {
            name: 'amount',
            type: FieldType.NUMBER,
            label: 'Cantidad',
            loading: !loaded,
            render: () => <Async
                loadFn={async () => {
                    const { loadProductStocks } = stockLoaders()
                    const productStocks = await loadProductStocks(define(product).id, stock.branchId)
                    const lotStock = productStocks.find(productStock => productStock.lotId === lot?.id)
                    const max = addMode ? undefined : lotStock?.availableUnits
                    return <>
                        <NumberField
                            id="amount"
                            min={1}
                            max={max}
                            integer={!isBulk}
                            className={styles.amount}
                            onChange={onChangeAmount}
                        />
                        {describeUnitType(product!, false, true)}
                    </>
                }}
                deps={[product, stock, lot]}
            />
        }
    ]
    if (lots.length > 0) {
        const lotOptions = lots.map(productLot => option(productLot.id, productLot.code))
        const lotField = {
            name: 'lotId',
            type: FieldType.TABLE,
            table: TableName.LOTS,
            label: 'Partida',
            options: lotOptions,
            defaultValue: lot?.id,
            onChange: onChangeLot
        }
        fields.splice(1, 0, lotField)
    }

    const onSubmit = () => {
        updateStock(define(product).id, define(amount), stock.branchId, define(lot).id, addMode)
    }

    const onCancel = () => navigate(-1)

    return (
        <PanelWrapper title={title} crumbs={crumbs}>
            <Form
                formId="stock-form"
                fields={fields}
                createMode={false}
                disableSubmit={!loaded}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        </PanelWrapper>
    )
}
